import { BsFileRuled } from 'react-icons/bs'
import { RiRedPacketLine } from 'react-icons/ri'
import { TbChartAreaLine, TbList, TbMessage, TbPassword } from 'react-icons/tb'
import { TDeviceType } from 'shared/types'
import { capitalizeFirstLetter } from 'shared/utils'
import { AlertRuleList } from './AlertRuleList'
import { DeviceIndicators } from './DeviceIndicators'
import { DeviceMeasurementList } from './DeviceMeasurementList'
import { LoraDeviceMessaging } from './messaging/LoraDeviceMessaging'
import { DevicePacketList } from './LoraDevicePacketList'
import DeviceKeysMap from './deviceKeys'
import { TFunction } from 'i18next'
import { Card, Stack, Tabs, TabsProps } from '@mantine/core'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactNode, useMemo } from 'react'
import { getDeviceRoute } from 'views/panel/DeviceGroups/Single/getDeviceRoute'
import { IconType } from 'react-icons'
import { useTranslation } from 'react-i18next'
import { MdOutlineNotificationImportant } from 'react-icons/md'
import { DeviceAlerts } from './DeviceAlerts'
import { networkProviderIds } from 'views/panel/Devices/RegisterDevice/networkProviders'

export type TAvailableDeviceTabs =
  | 'indicators'
  | 'alertRules'
  | 'measurements'
  | 'packets'
  | 'keys'
  | 'messaging'
  | 'alerts'

const items: (t: TFunction) => TabsItems<TAvailableDeviceTabs> = t => [
  {
    value: 'indicators',
    icon: TbChartAreaLine,
    allowedDeviceTypes: ['LoraDevice'],
    label: capitalizeFirstLetter(t('sensor.indicators')),
    children: ({ deviceModelId, deviceType }) => (
      <DeviceIndicators deviceModelId={deviceModelId} deviceType={deviceType} />
    ),
  },
  {
    value: 'alerts',
    icon: MdOutlineNotificationImportant,
    label: capitalizeFirstLetter(t('alert.plural')),
    allowedDeviceTypes: ['LoraDevice'],
    children: <DeviceAlerts />,
  },
  {
    value: 'measurements',
    icon: TbList,
    label: capitalizeFirstLetter(t('measurement.plural')),
    allowedDeviceTypes: ['LoraDevice'],
    children: <DeviceMeasurementList />,
  },
  {
    value: 'alertRules',
    icon: BsFileRuled,
    label: capitalizeFirstLetter(t('alert.templates')),
    allowedDeviceTypes: ['LoraDevice'],
    children: <AlertRuleList />,
  },
  {
    value: 'messaging',
    icon: TbMessage,
    label: t('sensor.messaging'),
    allowedDeviceTypes: ['LoraDevice'],
    hiddenNetworkProviderIds: [networkProviderIds.mqttBojki],
    children: ({ deviceModelId }) => (
      <LoraDeviceMessaging deviceModelId={deviceModelId} />
    ),
  },
  {
    value: 'packets',
    icon: RiRedPacketLine,
    label: capitalizeFirstLetter(t('packet.plural')),
    allowedDeviceTypes: ['SNMP', 'LoraDevice', 'LoraGateway'],
    children: ({ deviceType, networkProviderId }) => (
      <DevicePacketList
        deviceType={deviceType}
        networkProviderId={networkProviderId}
      />
    ),
  },
  {
    value: 'keys',
    icon: TbPassword,
    label: t('sensor.activationInfo'),
    children: ({ deviceType }) => <Card>{DeviceKeysMap[deviceType]}</Card>,
  },
]

export const DeviceTabs = ({
  deviceModelId,
  deviceType,
  networkProviderId,
}: {
  deviceType: TDeviceType
  deviceModelId: string | null
  networkProviderId?: number
}) => {
  const { t } = useTranslation()
  return (
    <TabsNav
      items={items(t)}
      defaultValue="indicators"
      deviceType={deviceType}
      deviceModelId={deviceModelId}
      networkProviderId={networkProviderId}
    />
  )
}

////////////////////////////////////////////////////////////////////////////

export type TabsItem<TKeys extends string> = {
  label: string
  icon: IconType
  value: TKeys
  allowedDeviceTypes?: TDeviceType[]
  hiddenNetworkProviderIds?: number[]
  children:
    | (({
        deviceModelId,
        deviceType,
        networkProviderId,
      }: {
        deviceModelId: string | null
        deviceType: TDeviceType
        networkProviderId?: number
      }) => ReactNode)
    | ReactNode
}

export type TabsItems<TKeys extends string> = TabsItem<TKeys>[]

type TabsNavProps<TKeys extends string> = {
  items: TabsItem<TKeys>[]
  defaultValue: TKeys
  deviceType: TDeviceType
  deviceModelId: string | null
  networkProviderId?: number
} & Omit<
  TabsProps,
  'defaultValue' | 'value' | 'onTabChange' | 'children' | 'ref'
>

const TabsNav = <T extends string>({
  items,
  defaultValue,
  deviceType,
  deviceModelId,
  networkProviderId,
  ...props
}: TabsNavProps<T>) => {
  const navigate = useNavigate()
  const { tabValue, deviceId } = useParams()

  const renderedItems = useMemo(
    () =>
      items
        .filter(
          item =>
            item.allowedDeviceTypes === undefined ||
            item.allowedDeviceTypes.includes(deviceType),
        )
        .filter(
          item =>
            item.hiddenNetworkProviderIds === undefined ||
            networkProviderId === undefined ||
            !item.hiddenNetworkProviderIds.includes(networkProviderId),
        ),
    [items, deviceType, networkProviderId],
  )

  const currentTab = useMemo(
    () =>
      renderedItems.find(item => item.value === tabValue) ??
      renderedItems.find(item => item.value === defaultValue),
    [defaultValue, renderedItems, tabValue],
  )

  return (
    <Stack>
      <Card p={'xs'}>
        <Tabs
          value={currentTab?.value ?? defaultValue}
          onChange={val =>
            navigate(
              getDeviceRoute(deviceType)(deviceId, val as TAvailableDeviceTabs),
            )
          }
          defaultValue={defaultValue}
          variant="pills"
          {...props}
        >
          <Tabs.List>
            {renderedItems.map(({ value, label, icon: Icon }) => (
              <Tabs.Tab
                key={value}
                leftSection={Icon && <Icon color="currentColor" size={14} />}
                value={value}
              >
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </Card>
      {currentTab &&
        (typeof currentTab.children === 'function'
          ? currentTab.children({
              deviceModelId,
              deviceType,
              networkProviderId,
            })
          : currentTab.children)}
    </Stack>
  )
}
